import React, { useEffect, useState } from 'react';
import { useNavigate, Outlet, Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Divider,
  Avatar,
  Chip,
  Badge,
} from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Map as MapIcon,
  Assessment as AssessmentIcon,
  CloudOff as CloudOffIcon,
  Settings as SettingsIcon,
  Logout,
  AdminPanelSettings as AdminIcon,
  People as PeopleIcon,
  CorporateFare as CompaniesIcon,
  History as AuditIcon,
  Description as ReportsIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { toast } from 'react-toastify';
import AppFooter from './AppFooter';
import GlobalSearch from '../GlobalSearch/GlobalSearch';
import { notificationsAPI } from '../../services/api';

const drawerWidth = 240;

const AppLayout: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, hasAnyRole } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [anchorElNotifications, setAnchorElNotifications] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const globalLoading = useSelector((state: RootState) => state.loading.global);
  const isUploaderOnly =
    hasAnyRole(['UPLOADER']) && !hasAnyRole(['ADMIN', 'SUPER_ADMIN', 'CORPORATE_ADMIN']);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = async () => {
    handleCloseUserMenu();
    try {
      await logout();
      navigate('/login');
      toast.success('Logged out successfully');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const loadNotifications = async () => {
    try {
      const [listRes, countRes] = await Promise.all([
        notificationsAPI.list({ unreadOnly: true, limit: 8 }),
        notificationsAPI.unreadCount(),
      ]);
      setNotifications(listRes.data?.data || []);
      setUnreadCount(countRes.data?.count || 0);
    } catch {
      // Keep notifications non-blocking for page rendering
    }
  };

  useEffect(() => {
    loadNotifications();
    const interval = window.setInterval(loadNotifications, 30000);
    return () => window.clearInterval(interval);
  }, []);

  // Navigation grouped by section (Overview, Entities, Operations, Admin)
  const overviewItems = isUploaderOnly
    ? [{ text: 'My Companies', icon: <DashboardIcon />, path: '/my-companies' }]
    : [{ text: 'Dashboard', icon: <DashboardIcon />, path: '/' }];
  const entitiesItems = isUploaderOnly
    ? []
    : [
        { text: 'Companies', icon: <CompaniesIcon />, path: '/companies' },
        { text: 'People', icon: <PeopleIcon />, path: '/people' },
        { text: 'Regions', icon: <MapIcon />, path: '/regions' },
      ];
  const operationsItems = isUploaderOnly
    ? []
    : [
        { text: 'Data Quality', icon: <AssessmentIcon />, path: '/data-quality' },
        { text: 'Wind-Up Tracker', icon: <CloudOffIcon />, path: '/wind-up' },
        { text: 'Reports', icon: <ReportsIcon />, path: '/reports' },
      ];
  const adminItems = hasAnyRole(['SUPER_ADMIN', 'ADMIN'])
    ? [
        { text: 'Admin', icon: <AdminIcon />, path: '/admin' },
        { text: 'Audit Logs', icon: <AuditIcon />, path: '/audit-logs' },
        { text: 'Permissions', icon: <SecurityIcon />, path: '/permissions' },
      ]
    : [];

  const renderNavList = (onItemClick?: () => void) => (
    <List>
      <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: 2 }}>Overview</ListSubheader>
      {overviewItems.map((item) => (
        <ListItem key={item.text} disablePadding>
          <ListItemButton component={RouterLink} to={item.path} onClick={onItemClick}>
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItemButton>
        </ListItem>
      ))}
      {entitiesItems.length > 0 && (
        <>
          <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: 2 }}>Entities</ListSubheader>
          {entitiesItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton component={RouterLink} to={item.path} onClick={onItemClick}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </>
      )}
      {operationsItems.length > 0 && (
        <>
          <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: 2 }}>Operations</ListSubheader>
          {operationsItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton component={RouterLink} to={item.path} onClick={onItemClick}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </>
      )}
      {adminItems.length > 0 && (
        <>
          <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: 2 }}>Admin</ListSubheader>
          {adminItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton component={RouterLink} to={item.path} onClick={onItemClick}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </>
      )}
    </List>
  );

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, flexShrink: 0 }}>
        <BusinessIcon color="primary" />
        <Typography variant="h6" component="div">
          AJGH
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ overflow: 'auto', flex: 1 }}>{renderNavList(handleDrawerToggle)}</Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ flexWrap: 'nowrap' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <BusinessIcon sx={{ display: { xs: 'none', md: 'flex' }, mr: 1 }} />
          <Typography
            variant="h6"
            noWrap
            component={RouterLink}
            to="/"
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' },
              fontWeight: 700,
              color: 'inherit',
              textDecoration: 'none',
            }}
          >
            AJ Group Holdings
          </Typography>

          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
            {!isUploaderOnly && <GlobalSearch />}
          </Box>

          {/* User Menu */}
          <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              aria-label="Open notifications"
              color="inherit"
              onClick={(event) => setAnchorElNotifications(event.currentTarget)}
            >
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <Menu
              anchorEl={anchorElNotifications}
              open={Boolean(anchorElNotifications)}
              onClose={() => setAnchorElNotifications(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { width: 340, maxWidth: '90vw' } }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle2">Notifications</Typography>
                <Typography variant="caption" color="text.secondary">
                  {unreadCount} unread
                </Typography>
              </Box>
              <Divider />
              {notifications.length === 0 ? (
                <MenuItem disabled>No unread notifications</MenuItem>
              ) : (
                notifications.map((notification) => (
                  <MenuItem
                    key={notification.id}
                    onClick={async () => {
                      try {
                        await notificationsAPI.markRead(notification.id);
                        if (notification.companyId) {
                          navigate(
                            isUploaderOnly
                              ? `/my-companies/${notification.companyId}`
                              : `/companies/${notification.companyId}`,
                          );
                        }
                        await loadNotifications();
                      } finally {
                        setAnchorElNotifications(null);
                      }
                    }}
                    sx={{ whiteSpace: 'normal', alignItems: 'flex-start' }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {notification.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {notification.message}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              )}
              <Divider />
              <MenuItem
                onClick={async () => {
                  await notificationsAPI.markAllRead();
                  await loadNotifications();
                }}
              >
                Mark all as read
              </MenuItem>
            </Menu>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2">{user?.fullName || user?.email}</Typography>
              <Typography variant="caption" color="inherit" sx={{ opacity: 0.8 }}>
                {user?.roles?.[0] || 'User'}
              </Typography>
            </Box>

            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }} aria-label="Open user menu">
              <Avatar alt={user?.fullName} src={user?.profilePicture}>
                {user?.firstName?.[0] || user?.email?.[0].toUpperCase()}
              </Avatar>
            </IconButton>

            <Menu
              sx={{ mt: '45px' }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle2">{user?.fullName || user?.email}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {user?.roles.map((role) => (
                    <Chip key={role} label={role} size="small" />
                  ))}
                </Box>
              </Box>
              <Divider />
              <MenuItem
                onClick={() => {
                  handleCloseUserMenu();
                  navigate('/account');
                }}
              >
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                Account Settings
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
        {globalLoading && <LinearProgress color="secondary" />}
      </AppBar>

      {/* Desktop: persistent sidebar with grouped nav */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            mt: '64px', // below AppBar
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          },
        }}
        open
      >
        {drawer}
      </Drawer>

      {/* Mobile: temporary drawer with same grouped nav */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 'min(280px, 85vw)',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main Content — offset on desktop so sidebar doesn't overlap; scrollable area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          minWidth: 0,
          marginLeft: { xs: 0, md: `${drawerWidth}px` },
          maxHeight: '100vh',
          minHeight: '100vh',
          bgcolor: 'grey.100',
        }}
      >
        <Toolbar /> {/* Spacer for fixed AppBar */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            p: { xs: 2, sm: 3 },
          }}
        >
          <Outlet />
        </Box>
        <AppFooter />
      </Box>
    </Box>
  );
};

export default AppLayout;
